import { useQuery, useQueryClient } from '@tanstack/react-query'
import { DataTable, useDataTableContext } from 'components/DataTable'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import apiService from 'services/api'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import * as S from '../CommonTable.styles'

import to from 'await-to-js'
import { AxiosError } from 'axios'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import TableButtonProduct from 'components/Buttons/TableButtons/TableButtonProduct'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import DistributorAssignModal from 'components/Modals/AssignmentModal/DistributorAssignModal'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import DoorExportModal from 'components/Modals/ExportModal/DoorExportModal'
import OpportunitiesTableFooterControls from 'components/Opportunities/OpportunitiesTableFooterControls'
import { DoorsFilterset } from 'components/Tables/DoorsTable/DoorsFilterset'
import { DOOR_SUMMARY_KEY } from 'constants/tableQueryKeys'
import { BsBriefcase, BsDownload, BsTags, BsTruck } from 'react-icons/bs'
import { PiCalendarStar, PiShippingContainer } from 'react-icons/pi'
import { notifyError } from 'services/toast'
import { useSelectedProductIds } from 'stores/SelectedProductsStore/SelectedProductsStore'
import { paramsContainAccountFilters } from 'utils/filterUtils'
import { useOpportunitiesContext } from '../../../pages/app/opportunities/opportunities-context'

import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { DataTableContainer } from '../../DataTable/UI'

import { TableSearchWithElastic } from '../../Filters/TableSearchWithElastic'
import { DoorMenuModal } from '../../Modals/MenuModal/DoorMenuModal'
import { Switch } from '../../UI/Switch/Switch'
import { cn } from '../../UI/cn'
import { ChangeProductViewModal } from '../../change-product-view-modal'
import { ManageTagsDistributorsParams } from 'services/common_table_actions'
import { SiSalesforce } from 'react-icons/si'
import { featureFlagService } from 'utils/featureFlagService'
import { toast } from 'react-toastify'
import { CreateDealModalForTable } from 'components/Deals/CreateDealModal/CreateDealModal'
import GPOAssignModal from 'components/Modals/AssignmentModal/GPOAssignModal'
import { useDoorsTableColumnsV2 } from './DoorsTableColumnsV2'
import { IDoorsTableProps } from './DoorsTable'
import { PlacesSummary } from 'models/summaries'
import ReminderModal from 'components/Reminders/ReminderModal'

const sortableFields = [
  'restaurant_name',
  'chain',
  'total_ltv',
  'brand_ltv',
  'revenue_ltv',
  'pounds_per_year',
  'reviews_count',
  'expense_category',
  'chain_count',
  'sales_stage',
  'account_owner',
  'dmanamecbsa',
  'avg_rating',
  'cuisine_50',
  'country',
  'state',
  'city',
  'zipcode',
  'domain',
  'velocity_group',
  'contact_count',
  'note_count',
  'taro',
  'tabo',
  'one_year_total_value',
  'distributors',
  'michelin_stars',
  'menu_size',
  'menu_matches',
  'sales_stages',
  'instagram_text_percentile',
  'hq_state1',
  'hq_city1',
  'hq_state2',
  'hq_city2',

  // TODO: Uncomment this when we have a better way to handle deal counts
  // 'deal_count',
]

const PAGE_SIZE = 100

export function DoorsTableComponentV2({
  hideTitle = true,
  ...props
}: IDoorsTableProps) {
  console.warn('im report doors!!')
  const featureFlag = featureFlagService()
  const tableContext = useDataTableContext()
  const queryClient = useQueryClient()
  const api = apiService()
  const isChainTable = props.chainFilter != null

  const { setRestaurantsView, chainsTabSelected, doorsTabSelected } =
    useOpportunitiesContext()
  const [menuModal, setMenuModal] = useState<
    | {
        doorId: number
        doorName?: string
        itemsIds?: number[]
        menuLength?: number
      }
    | undefined
  >(undefined)

  // TABLE CONTEXT (SORTING, ETC)
  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()
  const {
    state: { rowSelection, totalSelectedRows, isAllRowsSelected },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = tableContext

  const selectedProductsIds = useSelectedProductIds()

  const [showProductSelection, setShowProductSelection] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showAssignTags, setShowAssignTags] = useState(false)
  const [showCreateDealModal, setShowCreateDealModal] = useState(false)
  const [showAssignDistributors, setShowAssignDistributors] = useState(false)
  const [showAssignGPOs, setShowAssignGPOs] = useState(false)
  const [showExportAllModal, setShowExportAllModal] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)

  const restaurantsFilterStore = filterStoreRepo.getStore(
    'DOORS_CRM_REPORT_COLUMNS'
  )
  const { page, setPage } = restaurantsFilterStore()
  const filtersAsParams = useFilterParams(restaurantsFilterStore)

  // FILTER PARAMS (FOR API CALL)
  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = { ...filtersAsParams }

    if (props.chainFilter) {
      params.chain = props.chainFilter
    }

    return params
  }, [filtersAsParams, props.chainFilter])

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
    }),
    [filterParams, sortParams]
  )

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    page: page,
    products: selectedProductsIds,
  })

  // API CALL
  const {
    data: places,
    isFetching,
    refetch,
  } = useQuery({
    staleTime: Infinity,
    queryKey: TABLE_QUERY_KEY,
    enabled: doorsTabSelected || !!props.chainFilter, // Enable only when tab is selected or chain filter is present (chain page)
    queryFn: async ({ signal }) => {
      clearSelectedRows()
      const [err, res] = await to(
        api.getDoorsWithCRMReports(
          {
            ...filterAndSortParams,
            page: page,
            limit: PAGE_SIZE,
            product_id: selectedProductsIds,
          },
          signal
        )
      )

      if ((err as AxiosError)?.code === 'ERR_NETWORK') {
        notifyError('Sorry! Unable to complete this search, please try again.')
      }
      if (err) throw err

      return res.results
    },
  })

  useEffect(() => {
    clearSelectedRows()
  }, [places])

  // SUMMARY API CALL
  const {
    data: placesSummary = {
      total_ltv: 0,
      chains_count: 0,
      places_count: 0,
    } as PlacesSummary,
    isFetching: placesSummaryLoading,
  } = api.useGetDoorReportsSummary(
    {
      ...filterParams,
      product_id: selectedProductsIds.filter((p) => p !== -1),
    },
    undefined
  )

  // ˆ PASS LTV RESULTS TO PARENT COMPONENT ˆ
  useEffect(() => {
    props.setDoorsSummary?.({
      ...placesSummary,
      isLoading: placesSummaryLoading,
    })
    setTotalRowsInBackend(placesSummary.places_count ?? 0)
  }, [placesSummaryLoading, placesSummary])

  const selectedDoorIds = useMemo(() => {
    const accessor = 'restaurant.firstbite_id'
    return (
      places
        ?.filter((_, i: number) => rowSelection[i])

        ?.map((r: any) => {
          return accessor.split('.').reduce((acc, key) => acc[key], r)
        }) ?? []
    )
  }, [places, rowSelection])

  const unselectedDoorIds = useMemo(() => {
    if (!isAllRowsSelected) return []
    return (
      places
        ?.filter((_, i: number) => !rowSelection[i])
        ?.map((r: any) => r.restaurant.firstbite_id) ?? []
    )
  }, [places, rowSelection, isAllRowsSelected])

  const columns = useDoorsTableColumnsV2(
    setMenuModal,
    !!isChainTable,
    props.chainFilter,
    selectedProductsIds,
    sorting,
    false
  )

  const selectedDoorProxy = useMemo(() => {
    if (selectedDoorIds.length !== 1 || !places) return undefined
    const doorProxy = places.find(
      (item) => item.restaurant.firstbite_id === selectedDoorIds[0]
    )
    return doorProxy
  }, [selectedDoorIds, places])

  const refetchDoorsSummary = useCallback(() => {
    if (paramsContainAccountFilters(filterAndSortParams)) {
      queryClient.refetchQueries({ queryKey: [DOOR_SUMMARY_KEY] })
    }
  }, [filterAndSortParams, queryClient])

  const apiTagsAction = useCallback(
    async (values: ManageTagsDistributorsParams) => {
      const response = await api.commonManageTags({
        values,
        tableType: 'doors-reports-table',
      })
      refetchDoorsSummary()
      return response
    },
    [api, refetchDoorsSummary]
  )

  const apiDistributorsAction = useCallback(
    async (values: ManageTagsDistributorsParams) => {
      const response = await api.commonManageDistributors({
        tableType: 'doors-reports-table',
        values,
      })
      refetchDoorsSummary()
      return response
    },
    [api, refetchDoorsSummary]
  )

  const apiGPOsAction = useCallback(
    async (values: ManageTagsDistributorsParams) => {
      const response = await api.commonManageGPOs({
        tableType: 'doors-reports-table',
        values,
      })
      refetchDoorsSummary()
      return response
    },
    [api, refetchDoorsSummary]
  )

  return (
    <DataTableContainer>
      <DataTable
        tableHeader={
          <>
            {!hideTitle && (
              <Row className="mb-4">
                <Col>
                  <S.CardTitle>
                    Doors{' '}
                    <span style={{ fontWeight: 100 }}>
                      ({placesSummary?.places_count})
                    </span>
                  </S.CardTitle>
                </Col>
              </Row>
            )}
            <S.SearchContainer>
              <div className="flex w-full gap-2 items-center">
                <TableSearchWithElastic
                  filterStore={restaurantsFilterStore}
                  historyKey="doors"
                  options={[
                    {
                      label: 'Menu',
                      searchKey: 'menu',
                      searchPlaceholder: 'Type menu item',
                      elastic: true,
                    },
                    {
                      label: 'Name',
                      searchKey: 'restaurant_name',
                      searchPlaceholder: 'Type restaurant name',
                      elastic: true,
                    },
                  ]}
                />

                {!isChainTable ? (
                  <Switch
                    type={'checkbox'}
                    checked={chainsTabSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setRestaurantsView('chain')
                      } else {
                        setRestaurantsView('door')
                      }
                    }}
                    labelRight={chainsTabSelected ? 'Chain View' : 'Door View'}
                    className={'min-w-[120px]'}
                  />
                ) : null}

                <TableButtonProduct
                  selectedProducts={selectedProductsIds}
                  onClick={() => setShowProductSelection(true)}
                />

                <DoorsFilterset filterIdentifier="DOORS_CRM_REPORT_COLUMNS" />

                <ColumnSelectorRecipient tableKey={props.tableKey} />
              </div>

              <div className={cn('flex flex-row justify-between gap-4')}>
                <TableButtonAction
                  items={[
                    {
                      label: 'Create Deals',
                      icon: <BsBriefcase size={20} />,
                      callback: () => setShowCreateDealModal(true),
                    },
                    {
                      label: 'Manage Tags',
                      icon: <BsTags size={20} />,
                      callback: () => setShowAssignTags(true),
                    },
                    {
                      label: 'Add Known Distributors',
                      icon: <BsTruck size={20} />,
                      callback: () => setShowAssignDistributors(true),
                    },
                    {
                      label: 'Add GPO',
                      icon: <PiShippingContainer size={20} />,
                      callback: () => setShowAssignGPOs(true),
                    },
                    {
                      label: 'Schedule Reminder',
                      icon: <PiCalendarStar size={20} />,
                      callback: () => setShowReminderModal(true),
                      disabled: selectedDoorIds.length !== 1,
                      disabledTooltipText:
                        'Please select exactly one door to schedule a reminder',
                    },
                    {
                      label: 'Export Selected',
                      icon: <BsDownload size={20} />,
                      callback: () => setShowExportModal(true),
                    },
                    {
                      label: 'Push to Salesforce',
                      icon: <SiSalesforce size={20} />,
                      hidden: !featureFlag.enablePushToSalesforce,
                      callback: () => {
                        setTimeout(() => {
                          toast.success('Successfully pushed to Salesforce')
                        }, 1000)
                      },
                    },
                  ]}
                  disabled={!totalSelectedRows}
                  selectedRowsCount={totalSelectedRows ?? 0}
                />
                <FilterChips
                  identifier={'DOORS_CRM_REPORT_COLUMNS'}
                  clearAllButton
                  showActive
                  store={restaurantsFilterStore}
                />
              </div>
            </S.SearchContainer>

            <TagAssignModal
              show={showAssignTags}
              handleClose={() => setShowAssignTags(false)}
              tableQueryKey={TABLE_QUERY_KEY}
              data={places ?? []}
              filterAndSortParams={filterAndSortParams}
              apiTagsAction={apiTagsAction}
              idAccessor="door_proxy_id"
              tableObjAccessor="door_taglist"
            />

            <DistributorAssignModal
              show={showAssignDistributors}
              handleClose={() => setShowAssignDistributors(false)}
              tableQueryKey={TABLE_QUERY_KEY}
              data={places ?? []}
              idAccessor="door_proxy_id"
              filterAndSortParams={filterAndSortParams}
              apiDistributorsAction={apiDistributorsAction}
              tableObjAccessor="door_distributors"
            />

            <GPOAssignModal
              show={showAssignGPOs}
              handleClose={() => setShowAssignGPOs(false)}
              tableQueryKey={TABLE_QUERY_KEY}
              data={places ?? []}
              idAccessor="door_proxy_id"
              filterAndSortParams={filterAndSortParams}
              apiGPOsAction={apiGPOsAction}
              tableObjAccessor="door_gpos"
            />

            <CreateDealModalForTable
              optimisticUiTarget="none"
              show={showCreateDealModal}
              handleClose={() => setShowCreateDealModal(false)}
              onDealCreated={refetch}
              queryKey={TABLE_QUERY_KEY}
              filterAndSortParams={filterAndSortParams}
              data={places ?? []}
              idAccessor="chain_proxy.id"
              doorIdAccessor="door_proxy_id"
              companyTypeSlug={'restaurants-bars'}
              tableTypeSlug={'doors-reports-table'}
              forDoorSelection={true}
            />
          </>
        }
        tableKey={props.tableKey}
        data={places ?? []}
        loading={isFetching}
        columns={columns}
        sortableFields={sortableFields}
        virtualizeRows={true}
        enableRowSelection={!props.chainFilter}
        isPaginationEnabled={true}
        defaultSort={[{ id: 'chain_count', desc: true }]}
        paginationOptions={{
          totalRows: placesSummary?.places_count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: placesSummaryLoading,
        }}
        selectAllText="Select Opportunities"
        footerControls={
          <OpportunitiesTableFooterControls
            companyTypeSlug={'restaurants-bars'}
            chainProxy={props.chainProxy}
            unmatchedTableKey={props.unmatchedTableKey}
            onDownloadButtonClick={() =>
              chainsTabSelected
                ? setShowExportAllModal(true)
                : setShowExportModal(true)
            }
          />
        }
      />

      <ChangeProductViewModal
        show={showProductSelection}
        handleClose={() => setShowProductSelection(false)}
      />

      {menuModal?.doorId && (
        <DoorMenuModal
          filterStore={restaurantsFilterStore}
          show={!!menuModal}
          handleClose={() => setMenuModal(undefined)}
          doorId={menuModal.doorId!}
          doorName={menuModal.doorName!}
          menuLength={menuModal?.menuLength}
        />
      )}

      {featureFlag.enableReminders && (
        <ReminderModal
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          defaultDoorProxyId={selectedDoorProxy?.door_proxy_id}
          defaultCompanyId={selectedDoorProxy?.chain_proxy?.id}
          onCreated={() => {
            setShowReminderModal(false)
          }}
        />
      )}

      <DoorExportModal
        tableTypeSlug="doors-reports-table"
        filters={filterAndSortParams}
        productSelected={selectedProductsIds[0]}
        show={showExportModal || !!showExportAllModal}
        _handleClose={() => {
          setShowExportModal(false)
          setShowExportAllModal?.(false)
        }}
        selectedIds={
          showExportModal && !isAllRowsSelected ? selectedDoorIds : undefined
        }
        excludeIds={isAllRowsSelected ? unselectedDoorIds : undefined}
        count={
          showExportModal
            ? selectedDoorIds?.length
            : placesSummary?.places_count
        }
      />
    </DataTableContainer>
  )
}
